import type { AppLanguage } from '@/i18n/translations';
import type { PlanningInfoResponse } from '@/lib/api/assessment';
import { formatParcelCadastralLine } from '@/lib/planning-report-i18n';
import {
  isCommitteeMigrashLabel,
  isPlanNumberLabel,
} from '@/lib/validation/parcel';

type SectionsJson = PlanningInfoResponse['sections_json'] & {
  section_1?: { rows?: { label: string; value: string }[] };
  section_2?: { rows?: Record<string, string>[] };
  plan_catalog?: { plan_number?: string; is_primary?: boolean }[];
};

function extractPrimaryPlanNumber(sections?: SectionsJson): string | undefined {
  if (!sections) return undefined;

  const primaryFromCatalog = sections.plan_catalog?.find((p) => p.is_primary)?.plan_number;
  if (primaryFromCatalog?.trim()) return primaryFromCatalog.trim();

  const primaryRow = sections.section_1?.rows?.find(
    (r) => r.label === 'מספר תוכנית עיקרית',
  );
  if (primaryRow?.value?.trim()) return primaryRow.value.trim();

  const firstPlan = sections.section_2?.rows?.[0]?.['מספר תוכנית'];
  if (firstPlan?.trim()) return firstPlan.trim();

  return undefined;
}

export function resolvePlanningParcelDisplay(
  info: Pick<PlanningInfoResponse, 'migrash' | 'gush' | 'helka' | 'sections_json'>,
  labels: { migrash: string; planPrimary: string },
  language: AppLanguage,
): {
  committeeMigrash?: string;
  primaryPlan?: string;
  cadastralLine?: string;
  parcelLine?: string;
  documentRefSuffix?: string;
} {
  const committeeMigrash = isCommitteeMigrashLabel(info.migrash)
    ? info.migrash!.trim()
    : undefined;
  const primaryPlan =
    extractPrimaryPlanNumber(info.sections_json as SectionsJson) ??
    (info.migrash && isPlanNumberLabel(info.migrash) ? info.migrash.trim() : undefined);

  const cadastralLine =
    info.gush != null && info.helka != null
      ? formatParcelCadastralLine(info.gush, info.helka, language)
      : undefined;

  const parts: string[] = [];
  if (committeeMigrash) parts.push(`${labels.migrash}: ${committeeMigrash}`);
  if (primaryPlan) parts.push(`${labels.planPrimary}: ${primaryPlan}`);
  if (cadastralLine) parts.push(cadastralLine);

  const parcelLine = parts.length > 0 ? parts.join(' · ') : undefined;
  const documentRefSuffix =
    committeeMigrash ??
    primaryPlan ??
    (info.gush != null && info.helka != null ? `${info.gush}-${info.helka}` : undefined);

  return {
    committeeMigrash,
    primaryPlan,
    cadastralLine,
    parcelLine,
    documentRefSuffix,
  };
}
